import React from 'react';
import { Box, Typography, Button } from '@mui/material';
import { SvgIconComponent } from '@mui/icons-material';
import { alpha, useTheme } from '@mui/material/styles';

interface EmptyStateProps {
  /** Material-UI Icon-Component, z.B. Park, Warning, Star */
  icon: SvgIconComponent;
  /** Hauptzeile, z.B. "Noch keine Bäume erfasst" */
  title: string;
  /** Optionale Sub-Zeile, z.B. "Lege deinen ersten Baum auf der Karte an oder importiere eine CSV." */
  description?: string;
  /** Optional: Call-to-Action-Button */
  action?: {
    label: string;
    onClick: () => void;
    icon?: SvgIconComponent;
  };
  /** Größe – default 'medium' */
  size?: 'small' | 'medium' | 'large';
  /** Hintergrund-Farbe für den Icon-Kreis. Default: primary-tönung */
  accentColor?: 'primary' | 'success' | 'warning' | 'info' | 'error';
}

/**
 * Einheitlicher Leerzustand für die App:
 * - Großes farbiges Icon im Kreis
 * - Freundlicher Text
 * - Optional Call-to-Action
 *
 * Funktioniert in Light- und Dark-Mode (verwendet Theme-Tokens via alpha()).
 */
export default function EmptyState({
  icon: Icon,
  title,
  description,
  action,
  size = 'medium',
  accentColor = 'primary',
}: EmptyStateProps) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const accent = theme.palette[accentColor].main;

  const dims = {
    small:  { circle: 56, icon: 28, py: 3, titleVariant: 'subtitle2' as const, descVariant: 'caption' as const },
    medium: { circle: 80, icon: 40, py: 5, titleVariant: 'h6' as const,         descVariant: 'body2' as const },
    large:  { circle: 100, icon: 50, py: 7, titleVariant: 'h5' as const,        descVariant: 'body1' as const },
  }[size];

  const ActionIcon = action?.icon;

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      textAlign: 'center',
      py: dims.py,
      px: 2,
    }}>
      <Box sx={{
        width: dims.circle,
        height: dims.circle,
        borderRadius: '50%',
        bgcolor: alpha(accent, isDark ? 0.15 : 0.08),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        mb: 2,
      }}>
        <Icon sx={{ fontSize: dims.icon, color: accent, opacity: 0.85 }} />
      </Box>
      <Typography variant={dims.titleVariant} sx={{ fontWeight: 600, mb: description ? 0.5 : 0 }}>
        {title}
      </Typography>
      {description && (
        <Typography variant={dims.descVariant} color="text.secondary" sx={{ maxWidth: 420, mb: action ? 2.5 : 0 }}>
          {description}
        </Typography>
      )}
      {action && (
        <Button
          variant="contained"
          color={accentColor}
          onClick={action.onClick}
          startIcon={ActionIcon ? <ActionIcon /> : undefined}
          sx={{ mt: 1 }}
        >
          {action.label}
        </Button>
      )}
    </Box>
  );
}
